import React, { useMemo } from 'react';
import cx from 'classnames';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import Chip from 'lib/Chip';
import { Color } from 'lib/Chip/Chip.types';
import excludeSvg from 'assets/icons/exclude.svg';
import { POI } from 'components/common/types/Planner.types';
import { CUSTOM_POI_DATA_PROVIDER, MAX_VISIBLE_SAME_POI_CATEGORY_CHIPS_BEFORE_COMPRESSION } from './consts';
import { POIChipWithTooltipProps } from './PointsOfInterest.types';

const POIChipWithTooltip = ({
  include,
  label,
  removeValues,
  subLabel,
  radius,
  tooltip,
  onRemovePoiChip,
  isDisabled,
  isRemoveChipDisabled,
}: POIChipWithTooltipProps): React.ReactElement => {
  let chipColor = Color.PRIMARY;
  if (isDisabled) chipColor = Color.DISABLED;
  if (!include) chipColor = Color.GREY;

  return (
    <div className="flex gap-1" data-test-id="selected-poi">
      <Tooltip
        direction={TooltipDirection.BOTTOM}
        tooltip={
          <div className="flex flex-col gap-y-1">
            {tooltip.map((value) => (
              <span key={value}>{value}</span>
            ))}
          </div>
        }
      >
        <div>
          <Chip
            label={label}
            subLabel={subLabel}
            color={chipColor}
            icon={!include ? excludeSvg : null}
            onRemove={onRemovePoiChip ? () => onRemovePoiChip(removeValues ?? [label], include) : undefined}
            isDisabled={isDisabled || isRemoveChipDisabled}
          />
        </div>
      </Tooltip>
      {radius && <Chip label={`${radius}m`} color={chipColor} />}
    </div>
  );
};

const renderChipGroup = ({
  pois,
  include,
  key,
  groupKey,
  isCompressed,
  onRemovePoiChip,
  groupRadius,
  isDisabled,
}: {
  pois: POI[];
  include: boolean;
  key: string;
  groupKey: string;
  isCompressed: boolean;
  onRemovePoiChip?: (poiToRemove?: string[]) => void;
  groupRadius: number | undefined;
  isDisabled?: boolean;
}): React.ReactNode => {
  if (pois.length === 0) return null;

  if (isCompressed) {
    return (
      <POIChipWithTooltip
        include={include}
        key={key}
        label={groupKey}
        removeValues={pois.map(({ name }) => name)}
        subLabel={`${pois.length}`}
        radius={groupRadius}
        tooltip={pois.map(({ name, radius }) => `${name} - ${radius}m`)}
        isDisabled={isDisabled}
        onRemovePoiChip={onRemovePoiChip}
      />
    );
  }

  return pois.map(({ name, radius, tooltip, categoryPath }) => (
    <POIChipWithTooltip
      key={`${key}-${categoryPath || name}`}
      include={include}
      label={name}
      radius={radius}
      tooltip={[tooltip || '']}
      isDisabled={isDisabled}
      onRemovePoiChip={onRemovePoiChip}
    />
  ));
};

export const POIChipList: React.FC<{
  lineId?: string;
  selectedPOIByParentCategory: Record<string, { excluded: POI[]; included: POI[] }>;
  maxSelected?: number;
  onRemovePoiChip?: (poiToRemove?: string[]) => void;
  chipAlign?: 'items-end' | 'items-start';
  isDisabled?: boolean;
}> = ({
  lineId,
  selectedPOIByParentCategory,
  maxSelected = MAX_VISIBLE_SAME_POI_CATEGORY_CHIPS_BEFORE_COMPRESSION,
  chipAlign = 'items-end',
  onRemovePoiChip,
  isDisabled = false,
}) => {
  const content = useMemo(
    () =>
      Object.entries(selectedPOIByParentCategory).map(([key, items]) => {
        const isCustomPOI =
          (items.included[0]?.dataProvider ?? items.excluded[0]?.dataProvider) === CUSTOM_POI_DATA_PROVIDER;
        const isIncludedCompressed = isCustomPOI
          ? items.included.length === 0 || items.included.every(({ radius }) => radius === items.included[0].radius)
          : items.included.length > maxSelected;

        const isExcludedCompressed = isCustomPOI
          ? items.excluded.length === 0 || items.excluded.every(({ radius }) => radius === items.excluded[0].radius)
          : items.excluded.length > maxSelected;

        return (
          <React.Fragment key={key}>
            {renderChipGroup({
              pois: items.included,
              include: true,
              groupKey: key,
              key: `${lineId}-${key}-included`,
              isCompressed: isIncludedCompressed,
              onRemovePoiChip,
              groupRadius: items.included[0]?.radius,
              isDisabled,
            })}
            {renderChipGroup({
              pois: items.excluded,
              include: false,
              groupKey: key,
              key: `${lineId}-${key}-excluded`,
              isCompressed: isExcludedCompressed,
              onRemovePoiChip,
              groupRadius: items.excluded[0]?.radius,
              isDisabled,
            })}
          </React.Fragment>
        );
      }),
    [selectedPOIByParentCategory, maxSelected, isDisabled],
  );

  return <div className={cx('flex w-full flex-col align-middle gap-1', chipAlign)}>{content}</div>;
};
